import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  addPageReviewComment,
  approvePageReview,
  createPageReview,
  fetchPageReview,
  fetchPageReviews,
  requestChangesPageReview,
  resolvePageReviewComment,
  type PageReviewCommentRecord,
  type PageReviewDetailRecord,
  type ReviewAnchorPosition,
} from "@/hooks/use-page-reviews";

interface ReviewBlock {
  componentId: string;
  type: string;
  title: string;
}

function extractReviewBlocks(puckData: PageReviewDetailRecord["version"]["puckData"]): ReviewBlock[] {
  const content = Array.isArray(puckData?.content) ? puckData.content : [];

  return content.map((component, index) => {
    const props = component.props ?? {};
    const componentId = typeof props.id === "string" ? props.id : `${component.type}-${index + 1}`;
    const title =
      (typeof props.heading === "string" && props.heading)
      || (typeof props.title === "string" && props.title)
      || (typeof props.headline === "string" && props.headline)
      || (typeof props.label === "string" && props.label)
      || component.type;

    return {
      componentId,
      type: component.type,
      title,
    };
  });
}

function statusTone(status: PageReviewDetailRecord["status"]) {
  if (status === "approved") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700";
  if (status === "changes_requested") return "border-amber-500/30 bg-amber-500/10 text-amber-700";
  return "border-border bg-muted/50 text-muted-foreground";
}

function ReviewWorkspace({
  detail,
  mode,
  actorName,
  onApprove,
  onRequestChanges,
  onAddComment,
  onResolveComment,
  shareUrl,
  focusedCommentId,
}: {
  detail: PageReviewDetailRecord;
  mode: "internal" | "public";
  actorName: string;
  onApprove: () => Promise<void>;
  onRequestChanges: () => Promise<void>;
  onAddComment: (componentId: string, body: string, position?: ReviewAnchorPosition) => Promise<void>;
  onResolveComment: (commentId: string) => Promise<void>;
  shareUrl?: string;
  focusedCommentId?: string | null;
}) {
  const [selectedComponentId, setSelectedComponentId] = useState<string>("");
  const [commentDraft, setCommentDraft] = useState("");
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(focusedCommentId ?? null);
  const commentRefs = useRef(new Map<string, HTMLDivElement>());
  const blocks = useMemo(() => extractReviewBlocks(detail.version.puckData), [detail.version.puckData]);

  useEffect(() => {
    if (!selectedComponentId && blocks[0]) {
      setSelectedComponentId(blocks[0].componentId);
    }
  }, [blocks, selectedComponentId]);

  useEffect(() => {
    if (!focusedCommentId) {
      return;
    }

    const targetComment = detail.comments.find((comment) => comment.id === focusedCommentId);
    if (!targetComment) {
      return;
    }

    setSelectedComponentId(targetComment.componentId);
    setHighlightedCommentId(targetComment.id);

    const timeoutId = window.setTimeout(() => {
      setHighlightedCommentId((current) => (current === targetComment.id ? null : current));
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [detail.comments, focusedCommentId]);

  const selectedBlock = blocks.find((block) => block.componentId === selectedComponentId) ?? blocks[0] ?? null;
  const comments = detail.comments.filter((comment) => comment.componentId === selectedBlock?.componentId);
  const previewExpiryLabel = detail.preview?.expiresAt
    ? new Date(detail.preview.expiresAt).toLocaleString()
    : null;

  useEffect(() => {
    if (!highlightedCommentId) {
      return;
    }

    const element = commentRefs.current.get(highlightedCommentId);
    element?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }, [comments, highlightedCommentId]);

  return (
    <div className="space-y-6">
      <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-[0.22em] ${statusTone(detail.status)}`}>
                {detail.status.replace("_", " ")}
              </span>
              <span className="text-sm text-muted-foreground">
                Page version v{detail.version.versionNumber} for {detail.page.title}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">{detail.page.title}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Shareable read-only review surface for <code>{detail.page.slug}</code>. Clients stay out of the editor but can still leave anchored feedback and make a decision.
              </p>
            </div>
            {detail.message ? (
              <div className="rounded-2xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
                {detail.message}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 text-sm">
            {mode === "public" ? (
              <div className="rounded-xl border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                Reviewer identity from token: <span className="font-medium text-foreground">{actorName}</span>
                {detail.reviewerEmail ? (
                  <>
                    {" · "}
                    <span className="font-medium text-foreground">{detail.reviewerEmail}</span>
                  </>
                ) : null}
              </div>
            ) : null}
            {shareUrl ? (
              <div className="rounded-xl border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                Share link: <span className="font-mono text-foreground">{shareUrl}</span>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => { void onRequestChanges(); }}
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 font-medium text-amber-700 transition hover:bg-amber-500/20"
              >
                Request changes
              </button>
              <button
                type="button"
                onClick={() => { void onApprove(); }}
                className="rounded-xl bg-primary px-4 py-2 font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_360px]">
        <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Read-only page view</h2>
              <p className="text-sm text-muted-foreground">The review route now loads the same generated preview artifact the editor opens. The token only adds review controls and comment context.</p>
            </div>
            <div className="text-right text-sm text-muted-foreground">
              <div>{blocks.length} blocks</div>
              {previewExpiryLabel ? <div>Preview refreshes until {previewExpiryLabel}</div> : null}
            </div>
          </div>

          <div className="overflow-hidden rounded-[20px] border border-border bg-slate-950 shadow-[0_24px_60px_-32px_rgba(15,23,42,0.8)]">
            <div className="flex items-center justify-between border-b border-white/10 bg-slate-900/95 px-4 py-3 text-xs uppercase tracking-[0.22em] text-slate-300">
              <span>Shared preview artifact</span>
              {detail.preview?.url ? (
                <a
                  href={detail.preview.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-white/15 px-2.5 py-1 font-medium tracking-[0.14em] text-slate-100 transition hover:border-white/30 hover:bg-white/5"
                >
                  Open raw preview
                </a>
              ) : null}
            </div>
            {detail.preview?.url ? (
              <iframe
                title={`${detail.page.title} review preview`}
                src={detail.preview.url}
                className="h-[720px] w-full bg-white"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : (
              <div className="flex h-[320px] items-center justify-center bg-slate-950 px-6 text-center text-sm text-slate-300">
                Preview artifact is still warming up. Reload the review to fetch the signed preview surface.
              </div>
            )}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {blocks.map((block) => {
              const unresolvedCount = detail.comments.filter((comment) => comment.componentId === block.componentId && !comment.resolved).length;
              const isSelected = block.componentId === selectedBlock?.componentId;

              return (
                <button
                  type="button"
                  key={block.componentId}
                  onClick={() => setSelectedComponentId(block.componentId)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    isSelected ? "border-primary bg-primary/5 shadow-sm" : "border-border bg-background hover:border-foreground/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{block.type}</div>
                      <h3 className="mt-2 text-base font-medium text-foreground">{block.title}</h3>
                      <p className="mt-1 font-mono text-xs text-muted-foreground">{block.componentId}</p>
                    </div>
                    {unresolvedCount > 0 ? (
                      <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700">
                        {unresolvedCount} open
                      </span>
                    ) : (
                      <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-700">
                        clear
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="rounded-[24px] border border-border bg-card p-6 shadow-sm xl:sticky xl:top-6 xl:self-start">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-foreground">Floating comment toolbar</h2>
            <p className="text-sm text-muted-foreground">
              {selectedBlock ? `Discussing ${selectedBlock.componentId}` : "Pick a block to review it."}
            </p>
          </div>

          {selectedBlock ? (
            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-border bg-background px-4 py-3">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{selectedBlock.type}</div>
                <div className="mt-1 text-sm font-medium text-foreground">{selectedBlock.title}</div>
              </div>

              <div className="space-y-3">
                {comments.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
                    No anchored comments yet.
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div
                      key={comment.id}
                      ref={(element) => {
                        if (element) {
                          commentRefs.current.set(comment.id, element);
                          return;
                        }
                        commentRefs.current.delete(comment.id);
                      }}
                      data-highlighted={highlightedCommentId === comment.id ? "true" : undefined}
                      className={`rounded-2xl border bg-background px-4 py-3 transition ${
                        highlightedCommentId === comment.id
                          ? "border-amber-500/40 bg-amber-500/5 shadow-[0_0_0_1px_rgba(245,158,11,0.18)]"
                          : "border-border"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-medium text-foreground">{comment.authorName}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => { void onResolveComment(comment.id); }}
                          disabled={comment.resolved}
                          className={`rounded-lg px-2.5 py-1 text-xs font-medium ${
                            comment.resolved
                              ? "bg-emerald-500/10 text-emerald-700"
                              : "border border-border bg-card text-muted-foreground hover:bg-accent"
                          }`}
                        >
                          {comment.resolved ? "Resolved" : "Resolve"}
                        </button>
                      </div>
                      <p className="mt-3 text-sm text-foreground">{comment.body}</p>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-3 rounded-2xl border border-border bg-background p-4">
                <label className="space-y-2 text-sm">
                  <span className="font-medium text-foreground">New comment</span>
                  <textarea
                    value={commentDraft}
                    onChange={(event) => setCommentDraft(event.target.value)}
                    rows={4}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2"
                    placeholder="Call out copy, hierarchy, or anything that needs attention."
                  />
                </label>
                <button
                  type="button"
                  disabled={!commentDraft.trim() || (mode === "public" && !actorName.trim())}
                  onClick={() => {
                    void onAddComment(selectedBlock.componentId, commentDraft, { x: 0.5, y: 0.5 }).then(() => setCommentDraft(""));
                  }}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
                >
                  Add anchored comment
                </button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

export default function PageReviewPage() {
  const { pageId } = useParams();
  const [searchParams] = useSearchParams();
  const [reviews, setReviews] = useState<Array<{ id: string; shareToken: string; status: string; reviewerName: string | null; requestedAt: string }>>([]);
  const [selectedReviewId, setSelectedReviewId] = useState<string>("");
  const [detail, setDetail] = useState<PageReviewDetailRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [message, setMessage] = useState("");
  const requestedReviewId = searchParams.get("reviewId") ?? "";
  const focusedCommentId = searchParams.get("commentId");

  const load = async (nextReviewId?: string) => {
    if (!pageId) return;
    try {
      const list = await fetchPageReviews(pageId);
      setReviews(list);
      const reviewId = nextReviewId ?? requestedReviewId ?? selectedReviewId ?? list[0]?.id ?? "";
      setSelectedReviewId(reviewId);
      if (reviewId) {
        setDetail(await fetchPageReview(pageId, reviewId));
      } else {
        setDetail(null);
      }
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Failed to load page review");
    }
  };

  useEffect(() => {
    void load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, requestedReviewId]);

  if (!pageId) {
    return <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">Page id is missing.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Client Review</h1>
          <p className="mt-2 text-sm text-muted-foreground">Create a real share link for the current saved page version, track its state, and resolve anchored feedback.</p>
        </div>
        <Link to={`/pages/editor/${pageId}`} className="rounded-xl border border-border px-4 py-2 text-sm text-foreground transition hover:bg-accent">
          Back to editor
        </Link>
      </div>

      <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.3fr)_auto]">
          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Reviewer name</span>
            <input value={reviewerName} onChange={(event) => setReviewerName(event.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2" />
          </label>
          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Reviewer email</span>
            <input value={reviewerEmail} onChange={(event) => setReviewerEmail(event.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2" />
          </label>
          <label className="space-y-2 text-sm">
            <span className="font-medium text-foreground">Context note</span>
            <input value={message} onChange={(event) => setMessage(event.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2" placeholder="Optional message for the reviewer" />
          </label>
          <button
            type="button"
            onClick={() => {
              void createPageReview(pageId, { reviewerName, reviewerEmail, message }).then((review) => load(review.id));
            }}
            className="self-end rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            Create review link
          </button>
        </div>

        {reviews.length > 0 ? (
          <div className="mt-5 flex flex-wrap gap-2">
            {reviews.map((review) => (
              <button
                type="button"
                key={review.id}
                onClick={() => {
                  setSelectedReviewId(review.id);
                  void load(review.id);
                }}
                className={`rounded-full border px-3 py-1.5 text-sm transition ${
                  review.id === selectedReviewId ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"
                }`}
              >
                {review.reviewerName ?? "Unnamed reviewer"} · {review.status}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      {error ? <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div> : null}

      {detail ? (
        <ReviewWorkspace
          detail={detail}
          mode="internal"
          actorName=""
          shareUrl={`${window.location.origin}/review/${detail.shareToken}`}
          onApprove={async () => {
            await approvePageReview(pageId, detail.id);
            await load(detail.id);
          }}
          onRequestChanges={async () => {
            await requestChangesPageReview(pageId, detail.id);
            await load(detail.id);
          }}
          onAddComment={async (componentId, body, position) => {
            await addPageReviewComment(pageId, detail.id, {
              versionId: detail.version.id,
              componentId,
              body,
              position,
            });
            await load(detail.id);
          }}
          onResolveComment={async (commentId) => {
            await resolvePageReviewComment(pageId, detail.id, commentId);
            await load(detail.id);
          }}
          focusedCommentId={focusedCommentId}
        />
      ) : (
        <div className="rounded-[24px] border border-dashed border-border bg-muted/20 p-8 text-sm text-muted-foreground">
          No review links yet. Create one against the current saved page version.
        </div>
      )}
    </div>
  );
}

export { ReviewWorkspace };
